import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Users, DollarSign, Database, Settings, GitBranch, 
  Activity, AlertTriangle, BarChart3, Globe, Lock, Monitor,
  Search, Download, Upload, RefreshCw, Filter, Eye, Edit,
  Trash2, Plus, Server, Zap, TrendingUp, UserCheck, MessageSquare,
  CreditCard, FileText, Bell, Calendar, Map, Languages, Coins, Mail,
  Code, Key, UserPlus, UserX, RotateCcw, Save, AlertCircle, CheckCircle,
  Megaphone, Image, Settings2, ShieldCheck, History, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';

const SuperAdminDashboard = ({ user, onLogout, onOpenSettings }) => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalGroups: 0,
    totalTransactions: 0,
    totalRevenue: 0,
    activeUsers: 0,
    systemHealth: 100,
    errorRate: 0,
    deployments: 0
  });
  
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [cashPayments, setCashPayments] = useState([]);
  const [cryptoPayments, setCryptoPayments] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [contentSettings, setContentSettings] = useState({});
  const [systemConfig, setSystemConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [codeContent, setCodeContent] = useState('');
  const [notifications, setNotifications] = useState([]);

  // Backend API URL
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch core admin data (these endpoints exist)
      const corePromises = [
        axios.get(`${API}/superadmin/stats`, { headers }).catch(() => ({ data: stats })),
        axios.get(`${API}/superadmin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/superadmin/groups`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/superadmin/transactions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/superadmin/system-logs`, { headers }).catch(() => ({ data: [] }))
      ];
      
      // Fetch enhanced data (these endpoints may not exist yet - use fallbacks)
      const enhancedPromises = [
        axios.get(`${API}/superadmin/cash-payments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/superadmin/crypto-payments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/superadmin/audit`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/superadmin/content`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/superadmin/config`, { headers }).catch(() => ({ data: {} }))
      ];
      
      const [
        statsRes, 
        usersRes, 
        groupsRes, 
        transactionsRes,
        logsRes,
        cashPaymentsRes,
        cryptoPaymentsRes,
        auditRes,
        contentRes,
        configRes
      ] = await Promise.all([...corePromises, ...enhancedPromises]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setGroups(groupsRes.data);
      setTransactions(transactionsRes.data);
      setSystemLogs(logsRes.data);
      setCashPayments(cashPaymentsRes.data);
      setCryptoPayments(cryptoPaymentsRes.data);
      setAuditLogs(auditRes.data);
      setContentSettings(contentRes.data);
      setSystemConfig(configRes.data);
      
      // Log successful data fetch
      console.log('✅ SuperAdminDashboard data loaded successfully:', {
        users: usersRes.data.length,
        groups: groupsRes.data.length,
        transactions: transactionsRes.data.length,
        stats: statsRes.data
      });
      
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      setNotifications(prev => [...prev, {
        type: 'error',
        message: 'Erreur lors du chargement des données',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (userId, action) => {
    if (action === 'delete' || action === 'suspend') {
      setConfirmAction({ userId, action, type: 'user' });
      setShowConfirmDialog(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/superadmin/users/${userId}/action`, {
        action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log audit action
      await logAuditAction(`User ${action}`, `User ID: ${userId}`, 'user_management');
      
      setNotifications(prev => [...prev, {
        type: 'success',
        message: `Action ${action} exécutée avec succès sur l'utilisateur`,
        timestamp: new Date().toISOString()
      }]);
      
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error(`Error executing user action ${action}:`, error);
      setNotifications(prev => [...prev, {
        type: 'error',
        message: `Erreur lors de l'action ${action}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/superadmin/users/create`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await logAuditAction('User Created', `New user: ${userData.email}`, 'user_management');
      
      setNotifications(prev => [...prev, {
        type: 'success',
        message: 'Utilisateur créé avec succès',
        timestamp: new Date().toISOString()
      }]);
      
      setShowUserDialog(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error creating user:', error);
      setNotifications(prev => [...prev, {
        type: 'error',
        message: 'Erreur lors de la création de l\'utilisateur',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const handlePaymentValidation = async (paymentId, paymentType, action) => {
    if (action === 'reject') {
      setConfirmAction({ paymentId, paymentType, action, type: 'payment' });
      setShowConfirmDialog(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = paymentType === 'cash' ? 'cash-payments' : 'crypto-payments';
      
      await axios.post(`${API}/superadmin/${endpoint}/${paymentId}/validate`, {
        action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await logAuditAction(`Payment ${action}ed`, `${paymentType} payment ID: ${paymentId}`, 'payment_validation');
      
      setNotifications(prev => [...prev, {
        type: 'success',
        message: `Paiement ${action === 'approve' ? 'approuvé' : 'rejeté'} avec succès`,
        timestamp: new Date().toISOString()
      }]);
      
      fetchDashboardData();
    } catch (error) {
      console.error(`Error validating payment:`, error);
      setNotifications(prev => [...prev, {
        type: 'error',
        message: 'Erreur lors de la validation du paiement',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const logAuditAction = async (action, details, category) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/audit`, {
        action,
        details,
        category,
        admin_id: user.id,
        admin_name: user.username,
        timestamp: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error logging audit action:', error);
    }
  };

  const handleSystemConfigUpdate = async (configKey, configValue) => {
    setConfirmAction({ configKey, configValue, type: 'config' });
    setShowConfirmDialog(true);
  };

  const updateContentSettings = async (settingKey, settingValue) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/content/update`, {
        key: settingKey,
        value: settingValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await logAuditAction('Content Updated', `Updated ${settingKey}`, 'content_management');
      
      setNotifications(prev => [...prev, {
        type: 'success',
        message: 'Contenu mis à jour avec succès',
        timestamp: new Date().toISOString()
      }]);
      
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating content:', error);
      setNotifications(prev => [...prev, {
        type: 'error',
        message: 'Erreur lors de la mise à jour du contenu',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const executeConfirmedAction = async () => {
    if (!confirmAction) return;

    try {
      const token = localStorage.getItem('token');
      
      if (confirmAction.type === 'user') {
        await axios.post(`${API}/superadmin/users/${confirmAction.userId}/action`, {
          action: confirmAction.action
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        await logAuditAction(`User ${confirmAction.action}`, `User ID: ${confirmAction.userId}`, 'user_management');
        
      } else if (confirmAction.type === 'payment') {
        const endpoint = confirmAction.paymentType === 'cash' ? 'cash-payments' : 'crypto-payments';
        await axios.post(`${API}/superadmin/${endpoint}/${confirmAction.paymentId}/validate`, {
          action: confirmAction.action
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        await logAuditAction(`Payment ${confirmAction.action}ed`, `${confirmAction.paymentType} payment ID: ${confirmAction.paymentId}`, 'payment_validation');
        
      } else if (confirmAction.type === 'config') {
        await axios.post(`${API}/superadmin/config/update`, {
          key: confirmAction.configKey,
          value: confirmAction.configValue
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        await logAuditAction('Config Updated', `Updated ${confirmAction.configKey}`, 'system_config');
      }
      
      setNotifications(prev => [...prev, {
        type: 'success',
        message: 'Action critique exécutée avec succès',
        timestamp: new Date().toISOString()
      }]);
      
      fetchDashboardData();
    } catch (error) {
      console.error('Error executing confirmed action:', error);
      setNotifications(prev => [...prev, {
        type: 'error',
        message: 'Erreur lors de l\'exécution de l\'action',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const handleSystemAction = async (action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/system/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Action ${action} exécutée avec succès`);
    } catch (error) {
      console.error(`Error executing ${action}:`, error);
    }
  };

  const exportData = async (dataType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/export/${dataType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `likico_${dataType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(`Error exporting ${dataType}:`, error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-8 rounded-lg flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
            <p className="text-white">Chargement du tableau de bord...</p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white flex items-center gap-3">
            <Shield className="h-10 w-10 text-purple-400" />
            Super Admin Dashboard
          </h1>
          <p className="text-purple-200 mt-2">Gestion Globale Likico • {user?.username || user?.email}</p>
          <p className="text-sm text-slate-400 mt-1">
            Rôle: {user?.role} | Email: {user?.email} | Statut: Connecté ✅
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={() => window.open('https://github.com/likico/app', '_blank')}
            variant="outline"
            className="bg-slate-800 border-purple-500 text-purple-300 hover:bg-purple-800"
          >
            <GitBranch className="h-4 w-4 mr-2" />
            Code Source
          </Button>
          <Button
            onClick={onOpenSettings}
            variant="outline"
            className="bg-slate-800 border-gray-500 text-gray-300 hover:bg-gray-700"
            title="Paramètres"
          >
            <Settings className="h-4 w-4 mr-2" />
            Paramètres
          </Button>
          <Button
            onClick={onLogout}
            variant="outline"
            className="bg-red-800 border-red-500 text-red-300 hover:bg-red-700"
          >
            Déconnexion
          </Button>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-800 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">Utilisateurs Total</p>
                <p className="text-3xl font-bold">{stats.totalUsers.toLocaleString()}</p>
              </div>
              <Users className="h-12 w-12 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-600 to-green-800 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">Groupes Actifs</p>
                <p className="text-3xl font-bold">{stats.totalGroups.toLocaleString()}</p>
              </div>
              <Globe className="h-12 w-12 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-600 to-purple-800 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">Transactions</p>
                <p className="text-3xl font-bold">{stats.totalTransactions.toLocaleString()}</p>
              </div>
              <CreditCard className="h-12 w-12 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-600 to-orange-800 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100">Revenus Totaux</p>
                <p className="text-3xl font-bold">KES {stats.totalRevenue.toLocaleString()}</p>
              </div>
              <DollarSign className="h-12 w-12 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2 bg-slate-800 border-slate-700 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-400" />
              État du Système
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{stats.systemHealth}%</div>
                <div className="text-sm text-slate-300">Santé Système</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{stats.activeUsers}</div>
                <div className="text-sm text-slate-300">Utilisateurs Actifs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">{stats.errorRate}%</div>
                <div className="text-sm text-slate-300">Taux d'Erreur</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">{stats.deployments}</div>
                <div className="text-sm text-slate-300">Déploiements</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-400" />
              Actions Rapides
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={() => handleSystemAction('backup')}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Database className="h-4 w-4 mr-2" />
              Sauvegarde Système
            </Button>
            <Button 
              onClick={() => handleSystemAction('deploy')}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Déployer Mise à Jour
            </Button>
            <Button 
              onClick={() => exportData('all')}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Données
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card className="bg-slate-800 border-slate-700 text-white">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Gestion Centralisée</CardTitle>
            <div className="flex gap-2">
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 bg-slate-700 border-slate-600 text-white"
              />
              <Button
                onClick={fetchDashboardData}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid w-full grid-cols-8 bg-slate-700">
              <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600">Vue d'ensemble</TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-purple-600">Utilisateurs</TabsTrigger>
              <TabsTrigger value="groups" className="data-[state=active]:bg-purple-600">Groupes</TabsTrigger>
              <TabsTrigger value="payments" className="data-[state=active]:bg-purple-600">Paiements</TabsTrigger>
              <TabsTrigger value="content" className="data-[state=active]:bg-purple-600">Contenu</TabsTrigger>
              <TabsTrigger value="system" className="data-[state=active]:bg-purple-600">Système</TabsTrigger>
              <TabsTrigger value="audit" className="data-[state=active]:bg-purple-600">Audit</TabsTrigger>
              <TabsTrigger value="code" className="data-[state=active]:bg-purple-600">Code</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Charts and Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white">Croissance Utilisateurs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 bg-slate-800 rounded flex items-center justify-center">
                      <p className="text-slate-400">Graphique de croissance (à implémenter avec Chart.js)</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white">Revenus par Mois</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 bg-slate-800 rounded flex items-center justify-center">
                      <p className="text-slate-400">Graphique de revenus (à implémenter avec Chart.js)</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <Card className="bg-slate-700 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-white">Activité Récente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {systemLogs.slice(0, 5).map((log, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-800 rounded">
                        <Badge variant={log.level === 'error' ? 'destructive' : 'secondary'}>
                          {log.level}
                        </Badge>
                        <span className="text-white flex-1">{log.message}</span>
                        <span className="text-slate-400 text-sm">{log.timestamp}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="content" className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Gestion du Contenu</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* System Messages */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Megaphone className="h-5 w-5 text-blue-400" />
                      Messages Système
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Message d'accueil</label>
                      <Input
                        value={contentSettings.welcome_message || ''}
                        onChange={(e) => updateContentSettings('welcome_message', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="Bienvenue sur Likico..."
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Message de maintenance</label>
                      <Input
                        value={contentSettings.maintenance_message || ''}
                        onChange={(e) => updateContentSettings('maintenance_message', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="Maintenance en cours..."
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Conditions d'utilisation</label>
                      <textarea
                        value={contentSettings.terms_of_service || ''}
                        onChange={(e) => updateContentSettings('terms_of_service', e.target.value)}
                        className="w-full h-24 p-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                        placeholder="Conditions d'utilisation..."
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Advertisement Management */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Image className="h-5 w-5 text-green-400" />
                      Gestion Publicités
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">URL Publicité Homepage</label>
                      <Input
                        value={contentSettings.homepage_ad_url || ''}
                        onChange={(e) => updateContentSettings('homepage_ad_url', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="https://example.com/ad-image.jpg"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">URL Publicité Dashboard</label>
                      <Input
                        value={contentSettings.dashboard_ad_url || ''}
                        onChange={(e) => updateContentSettings('dashboard_ad_url', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="https://example.com/ad-image.jpg"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Fréquence affichage (ms)</label>
                      <Input
                        type="number"
                        value={contentSettings.ad_frequency || 30000}
                        onChange={(e) => updateContentSettings('ad_frequency', parseInt(e.target.value))}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="30000"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="ads_enabled"
                        checked={contentSettings.ads_enabled || false}
                        onChange={(e) => updateContentSettings('ads_enabled', e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="ads_enabled" className="text-sm text-slate-300">Publicités activées</label>
                    </div>
                  </CardContent>
                </Card>

                {/* Email Templates */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Mail className="h-5 w-5 text-purple-400" />
                      Modèles d'Email
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Email de bienvenue</label>
                      <textarea
                        value={contentSettings.welcome_email_template || ''}
                        onChange={(e) => updateContentSettings('welcome_email_template', e.target.value)}
                        className="w-full h-20 p-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                        placeholder="Bonjour {{username}}, bienvenue sur Likico..."
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Email de confirmation de paiement</label>
                      <textarea
                        value={contentSettings.payment_confirmation_email || ''}
                        onChange={(e) => updateContentSettings('payment_confirmation_email', e.target.value)}
                        className="w-full h-20 p-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                        placeholder="Votre paiement de {{amount}} a été confirmé..."
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Notification Settings */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Bell className="h-5 w-5 text-yellow-400" />
                      Paramètres Notifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="email_notifications"
                          checked={contentSettings.email_notifications_enabled || false}
                          onChange={(e) => updateContentSettings('email_notifications_enabled', e.target.checked)}
                          className="rounded"
                        />
                        <label htmlFor="email_notifications" className="text-sm text-slate-300">Notifications par email</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="sms_notifications"
                          checked={contentSettings.sms_notifications_enabled || false}
                          onChange={(e) => updateContentSettings('sms_notifications_enabled', e.target.checked)}
                          className="rounded"
                        />
                        <label htmlFor="sms_notifications" className="text-sm text-slate-300">Notifications par SMS</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="push_notifications"
                          checked={contentSettings.push_notifications_enabled || false}
                          onChange={(e) => updateContentSettings('push_notifications_enabled', e.target.checked)}
                          className="rounded"
                        />
                        <label htmlFor="push_notifications" className="text-sm text-slate-300">Notifications push</label>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="users" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Gestion des Utilisateurs</h3>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      setSelectedUser(null);
                      setShowUserDialog(true);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer Utilisateur
                  </Button>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="active">Actifs</SelectItem>
                      <SelectItem value="suspended">Suspendus</SelectItem>
                      <SelectItem value="admin">Admins</SelectItem>
                      <SelectItem value="super_admin">Super Admins</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => exportData('users')} variant="outline" className="border-slate-600">
                    <Download className="h-4 w-4 mr-2" />
                    Exporter
                  </Button>
                </div>
              </div>

              <Card className="bg-slate-700 border-slate-600">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-600">
                        <TableHead className="text-slate-300">ID</TableHead>
                        <TableHead className="text-slate-300">Nom d'utilisateur</TableHead>
                        <TableHead className="text-slate-300">Email</TableHead>
                        <TableHead className="text-slate-300">Statut</TableHead>
                        <TableHead className="text-slate-300">Rôle</TableHead>
                        <TableHead className="text-slate-300">Inscription</TableHead>
                        <TableHead className="text-slate-300">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.filter(user => 
                        filterStatus === 'all' || user.status === filterStatus || user.role === filterStatus
                      ).filter(user =>
                        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
                      ).map((user) => (
                        <TableRow key={user.id} className="border-slate-600">
                          <TableCell className="text-white font-mono text-xs">{user.id}</TableCell>
                          <TableCell className="text-white">{user.username}</TableCell>
                          <TableCell className="text-white">{user.email}</TableCell>
                          <TableCell>
                            <Badge variant={user.status === 'active' ? 'default' : 'destructive'}>
                              {user.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.role === 'super_admin' ? 'destructive' : user.role === 'admin' ? 'secondary' : 'outline'}>
                              {user.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-white">{new Date(user.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowUserDialog(true);
                                }}
                                className="border-slate-600 p-1"
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUserAction(user.id, user.status === 'active' ? 'suspend' : 'activate')}
                                className={`border-slate-600 p-1 ${user.status === 'active' ? 'hover:bg-yellow-700' : 'hover:bg-green-700'}`}
                              >
                                {user.status === 'active' ? <UserX className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />}
                              </Button>
                              {user.role !== 'super_admin' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUserAction(user.id, 'delete')}
                                  className="border-slate-600 p-1 hover:bg-red-700"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="groups" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Gestion des Groupes</h3>
                <Button onClick={() => exportData('groups')} variant="outline" className="border-slate-600">
                  <Download className="h-4 w-4 mr-2" />
                  Exporter Groupes
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groups.filter(group =>
                  group.name.toLowerCase().includes(searchTerm.toLowerCase())
                ).map((group) => (
                  <Card key={group.id} className="bg-slate-700 border-slate-600">
                    <CardHeader>
                      <CardTitle className="text-white text-lg">{group.name}</CardTitle>
                      <CardDescription className="text-slate-300">
                        Admin: {group.admin_name}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-white">
                          <span>Membres:</span>
                          <span>{group.member_count}/{group.max_members}</span>
                        </div>
                        <div className="flex justify-between text-white">
                          <span>Contribution:</span>
                          <span>{group.currency} {group.contribution_amount}</span>
                        </div>
                        <div className="flex justify-between text-white">
                          <span>Statut:</span>
                          <Badge variant={group.status === 'active' ? 'default' : 'secondary'}>
                            {group.status}
                          </Badge>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button size="sm" variant="outline" className="flex-1 border-slate-600">
                            <Eye className="h-3 w-3 mr-1" />
                            Voir
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1 border-slate-600">
                            <Edit className="h-3 w-3 mr-1" />
                            Modifier
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="payments" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Gestion des Paiements</h3>
                <div className="flex gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="cash">Espèces</SelectItem>
                      <SelectItem value="crypto">Crypto</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="approved">Approuvés</SelectItem>
                      <SelectItem value="rejected">Rejetés</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => exportData('payments')} variant="outline" className="border-slate-600">
                    <Download className="h-4 w-4 mr-2" />
                    Exporter
                  </Button>
                </div>
              </div>

              {/* Cash Payments Section */}
              <Card className="bg-slate-700 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-400" />
                    Paiements en Espèces - Validation Manuelle
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-600">
                        <TableHead className="text-slate-300">ID</TableHead>
                        <TableHead className="text-slate-300">Utilisateur</TableHead>
                        <TableHead className="text-slate-300">Groupe</TableHead>
                        <TableHead className="text-slate-300">Montant</TableHead>
                        <TableHead className="text-slate-300">Statut</TableHead>
                        <TableHead className="text-slate-300">Date</TableHead>
                        <TableHead className="text-slate-300">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cashPayments.filter(payment => 
                        filterStatus === 'all' || 
                        filterStatus === 'cash' || 
                        payment.status === filterStatus
                      ).map((payment) => (
                        <TableRow key={payment.id} className="border-slate-600">
                          <TableCell className="text-white font-mono text-xs">{payment.id}</TableCell>
                          <TableCell className="text-white">{payment.user_name}</TableCell>
                          <TableCell className="text-white">{payment.group_name}</TableCell>
                          <TableCell className="text-white">
                            {payment.currency} {payment.amount?.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              payment.status === 'approved' ? 'default' : 
                              payment.status === 'pending' ? 'secondary' : 'destructive'
                            }>
                              {payment.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-white">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {payment.status === 'pending' && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  onClick={() => handlePaymentValidation(payment.id, 'cash', 'approve')}
                                  className="bg-green-600 hover:bg-green-700 p-1"
                                >
                                  <CheckCircle className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handlePaymentValidation(payment.id, 'cash', 'reject')}
                                  className="bg-red-600 hover:bg-red-700 p-1"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Crypto Payments Section */}
              <Card className="bg-slate-700 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Coins className="h-5 w-5 text-orange-400" />
                    Paiements Cryptomonnaies - Vérification Blockchain
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-600">
                        <TableHead className="text-slate-300">ID</TableHead>
                        <TableHead className="text-slate-300">Utilisateur</TableHead>
                        <TableHead className="text-slate-300">Groupe</TableHead>
                        <TableHead className="text-slate-300">Montant</TableHead>
                        <TableHead className="text-slate-300">Token</TableHead>
                        <TableHead className="text-slate-300">TX Hash</TableHead>
                        <TableHead className="text-slate-300">Statut</TableHead>
                        <TableHead className="text-slate-300">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cryptoPayments.filter(payment => 
                        filterStatus === 'all' || 
                        filterStatus === 'crypto' || 
                        payment.status === filterStatus
                      ).map((payment) => (
                        <TableRow key={payment.id} className="border-slate-600">
                          <TableCell className="text-white font-mono text-xs">{payment.id}</TableCell>
                          <TableCell className="text-white">{payment.user_name}</TableCell>
                          <TableCell className="text-white">{payment.group_name}</TableCell>
                          <TableCell className="text-white">
                            {payment.amount} {payment.token_symbol}
                          </TableCell>
                          <TableCell className="text-white">{payment.token_symbol}</TableCell>
                          <TableCell className="text-white">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs">{payment.tx_hash?.substring(0, 10)}...</span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`https://etherscan.io/tx/${payment.tx_hash}`, '_blank')}
                                className="border-slate-600 p-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              payment.status === 'confirmed' ? 'default' : 
                              payment.status === 'pending' ? 'secondary' : 'destructive'
                            }>
                              {payment.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {payment.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handlePaymentValidation(payment.id, 'crypto', 'verify')}
                                className="bg-blue-600 hover:bg-blue-700 p-1"
                              >
                                <RefreshCw className="h-3 w-3" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="system" className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Administration Système</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white">Serveur & Infrastructure</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      onClick={() => handleSystemAction('restart')}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Redémarrer Serveur
                    </Button>
                    <Button 
                      onClick={() => handleSystemAction('clear-cache')}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Vider Cache
                    </Button>
                    <Button 
                      onClick={() => handleSystemAction('maintenance')}
                      className="w-full bg-yellow-600 hover:bg-yellow-700"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Mode Maintenance
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white">Base de Données</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      onClick={() => handleSystemAction('backup-db')}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <Database className="h-4 w-4 mr-2" />
                      Sauvegarde BD
                    </Button>
                    <Button 
                      onClick={() => handleSystemAction('optimize-db')}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Optimiser BD
                    </Button>
                    <Button 
                      onClick={() => exportData('database')}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export Complet
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* System Logs */}
              <Card className="bg-slate-700 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-white">Journaux Système</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {systemLogs.map((log, index) => (
                      <div key={index} className="flex items-start gap-3 p-2 bg-slate-800 rounded text-sm">
                        <Badge variant={log.level === 'error' ? 'destructive' : 'secondary'} className="mt-0.5">
                          {log.level}
                        </Badge>
                        <div className="flex-1">
                          <p className="text-white">{log.message}</p>
                          <p className="text-slate-400 text-xs">{log.timestamp}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Journaux d'Audit</h3>
                <div className="flex gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="all">Toutes</SelectItem>
                      <SelectItem value="user_management">Gestion Utilisateurs</SelectItem>
                      <SelectItem value="payment_validation">Validation Paiements</SelectItem>
                      <SelectItem value="system_config">Configuration Système</SelectItem>
                      <SelectItem value="content_management">Gestion Contenu</SelectItem>
                      <SelectItem value="group_management">Gestion Groupes</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => exportData('audit')} variant="outline" className="border-slate-600">
                    <Download className="h-4 w-4 mr-2" />
                    Exporter Audit
                  </Button>
                </div>
              </div>

              <Card className="bg-slate-700 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="h-5 w-5 text-blue-400" />
                    Historique des Actions Administratives
                  </CardTitle>
                  <CardDescription className="text-slate-300">
                    Traçabilité complète de toutes les actions critiques effectuées par les administrateurs
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-600">
                        <TableHead className="text-slate-300">Timestamp</TableHead>
                        <TableHead className="text-slate-300">Admin</TableHead>
                        <TableHead className="text-slate-300">Action</TableHead>
                        <TableHead className="text-slate-300">Détails</TableHead>
                        <TableHead className="text-slate-300">Catégorie</TableHead>
                        <TableHead className="text-slate-300">IP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.filter(log => 
                        filterStatus === 'all' || log.category === filterStatus
                      ).filter(log =>
                        log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        log.admin_name?.toLowerCase().includes(searchTerm.toLowerCase())
                      ).map((log, index) => (
                        <TableRow key={index} className="border-slate-600">
                          <TableCell className="text-white font-mono text-xs">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-white">
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4 text-purple-400" />
                              {log.admin_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              log.action?.includes('delete') || log.action?.includes('suspend') ? 'destructive' :
                              log.action?.includes('create') || log.action?.includes('approve') ? 'default' : 'secondary'
                            }>
                              {log.action}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-white max-w-xs truncate">
                            {log.details}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-slate-300">
                              {log.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-400 font-mono text-xs">
                            {log.ip_address || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Audit Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Activity className="h-8 w-8 text-green-400" />
                      <div>
                        <p className="text-2xl font-bold text-white">{auditLogs.length}</p>
                        <p className="text-sm text-slate-300">Actions Totales</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-8 w-8 text-yellow-400" />
                      <div>
                        <p className="text-2xl font-bold text-white">
                          {auditLogs.filter(log => log.action?.includes('delete') || log.action?.includes('suspend')).length}
                        </p>
                        <p className="text-sm text-slate-300">Actions Critiques</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-8 w-8 text-blue-400" />
                      <div>
                        <p className="text-2xl font-bold text-white">
                          {new Set(auditLogs.map(log => log.admin_name)).size}
                        </p>
                        <p className="text-sm text-slate-300">Admins Actifs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="code" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Gestion du Code & Configuration</h3>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setShowCodeEditor(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Code className="h-4 w-4 mr-2" />
                    Éditeur de Code
                  </Button>
                  <Button 
                    onClick={() => window.open('https://github.com/likico/app', '_blank')}
                    variant="outline"
                    className="border-slate-600"
                  >
                    <GitBranch className="h-4 w-4 mr-2" />
                    Dépôt GitHub
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* System Configuration */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Settings2 className="h-5 w-5 text-green-400" />
                      Configuration Système
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Nom de l'application</label>
                      <Input
                        value={systemConfig.app_name || 'Likico'}
                        onChange={(e) => handleSystemConfigUpdate('app_name', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Version</label>
                      <Input
                        value={systemConfig.app_version || '1.0.0'}
                        onChange={(e) => handleSystemConfigUpdate('app_version', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">URL API Backend</label>
                      <Input
                        value={systemConfig.backend_url || ''}
                        onChange={(e) => handleSystemConfigUpdate('backend_url', e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Environnement</label>
                      <select
                        value={systemConfig.environment || 'production'}
                        onChange={(e) => handleSystemConfigUpdate('environment', e.target.value)}
                        className="w-full p-2 bg-slate-800 border border-slate-600 rounded text-white"
                      >
                        <option value="development">Développement</option>
                        <option value="staging">Test</option>
                        <option value="production">Production</option>
                      </select>
                    </div>
                  </CardContent>
                </Card>

                {/* Security Configuration */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Lock className="h-5 w-5 text-red-400" />
                      Configuration Sécurité
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">JWT Secret (masqué)</label>
                      <Input
                        type="password"
                        value="************************************"
                        className="bg-slate-800 border-slate-600 text-white"
                        readOnly
                      />
                      <Button 
                        size="sm"
                        onClick={() => handleSystemConfigUpdate('jwt_secret', 'regenerate')}
                        className="bg-yellow-600 hover:bg-yellow-700"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Régénérer
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Expiration session (heures)</label>
                      <Input
                        type="number"
                        value={systemConfig.session_expiry_hours || 24}
                        onChange={(e) => handleSystemConfigUpdate('session_expiry_hours', parseInt(e.target.value))}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="two_factor_required"
                        checked={systemConfig.two_factor_required || false}
                        onChange={(e) => handleSystemConfigUpdate('two_factor_required', e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="two_factor_required" className="text-sm text-slate-300">
                        2FA obligatoire pour les admins
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="maintenance_mode"
                        checked={systemConfig.maintenance_mode || false}
                        onChange={(e) => handleSystemConfigUpdate('maintenance_mode', e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="maintenance_mode" className="text-sm text-slate-300">
                        Mode maintenance activé
                      </label>
                    </div>
                  </CardContent>
                </Card>

                {/* Database Configuration */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Database className="h-5 w-5 text-blue-400" />
                      Configuration Base de Données
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">URL MongoDB (masquée)</label>
                      <Input
                        type="password"
                        value="mongodb://****:****@****:****/****"
                        className="bg-slate-800 border-slate-600 text-white"
                        readOnly
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Pool de connexions max</label>
                      <Input
                        type="number"
                        value={systemConfig.db_max_connections || 10}
                        onChange={(e) => handleSystemConfigUpdate('db_max_connections', parseInt(e.target.value))}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="auto_backup"
                        checked={systemConfig.auto_backup_enabled || false}
                        onChange={(e) => handleSystemConfigUpdate('auto_backup_enabled', e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="auto_backup" className="text-sm text-slate-300">
                        Sauvegarde automatique quotidienne
                      </label>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Configuration */}
                <Card className="bg-slate-700 border-slate-600">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-purple-400" />
                      Configuration Paiements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Clé API M-Pesa (masquée)</label>
                      <Input
                        type="password"
                        value="************************************"
                        className="bg-slate-800 border-slate-600 text-white"
                        readOnly
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-300">Réseau Blockchain</label>
                      <select
                        value={systemConfig.blockchain_network || 'ethereum'}
                        onChange={(e) => handleSystemConfigUpdate('blockchain_network', e.target.value)}
                        className="w-full p-2 bg-slate-800 border border-slate-600 rounded text-white"
                      >
                        <option value="ethereum">Ethereum Mainnet</option>
                        <option value="polygon">Polygon</option>
                        <option value="bsc">Binance Smart Chain</option>
                        <option value="testnet">Testnet</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="crypto_payments_enabled"
                        checked={systemConfig.crypto_payments_enabled || false}
                        onChange={(e) => handleSystemConfigUpdate('crypto_payments_enabled', e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="crypto_payments_enabled" className="text-sm text-slate-300">
                        Paiements crypto activés
                      </label>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Notifications System */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 space-y-2 z-50">
          {notifications.slice(-3).map((notification, index) => (
            <Alert key={index} className={`w-96 ${
              notification.type === 'error' ? 'border-red-500 bg-red-900/20' : 'border-green-500 bg-green-900/20'
            }`}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-white">
                {notification.message}
                <span className="block text-xs text-slate-400 mt-1">
                  {new Date(notification.timestamp).toLocaleTimeString()}
                </span>
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-slate-800 border-slate-600 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              Confirmation Action Critique
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-300">
              {confirmAction?.type === 'user' && 
                `Êtes-vous sûr de vouloir ${confirmAction.action} cet utilisateur ? Cette action est irréversible.`}
              {confirmAction?.type === 'payment' && 
                `Êtes-vous sûr de vouloir ${confirmAction.action} ce paiement ${confirmAction.paymentType} ?`}
              {confirmAction?.type === 'config' && 
                `Êtes-vous sûr de vouloir modifier la configuration ${confirmAction.configKey} ? Cela peut affecter le fonctionnement de l'application.`}
            </p>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmDialog(false)}
                className="border-slate-600"
              >
                Annuler
              </Button>
              <Button 
                onClick={executeConfirmedAction}
                className="bg-red-600 hover:bg-red-700"
              >
                Confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Creation/Edit Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="bg-slate-800 border-slate-600 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? 'Modifier Utilisateur' : 'Créer Nouvel Utilisateur'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-300">Nom d'utilisateur</label>
                <Input
                  placeholder="nom_utilisateur"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-300">Email</label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-300">Rôle</label>
                <select className="w-full p-2 bg-slate-700 border border-slate-600 rounded text-white">
                  <option value="member">Membre</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-300">Statut</label>
                <select className="w-full p-2 bg-slate-700 border border-slate-600 rounded text-white">
                  <option value="active">Actif</option>
                  <option value="suspended">Suspendu</option>
                </select>
              </div>
            </div>
            {!selectedUser && (
              <div className="space-y-2">
                <label className="text-sm text-slate-300">Mot de passe temporaire</label>
                <Input
                  type="password"
                  placeholder="********"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowUserDialog(false)}
                className="border-slate-600"
              >
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  // handleCreateUser or handleUpdateUser logic here
                  setShowUserDialog(false);
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {selectedUser ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Code Editor Dialog */}
      <Dialog open={showCodeEditor} onOpenChange={setShowCodeEditor}>
        <DialogContent className="bg-slate-800 border-slate-600 text-white max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5 text-purple-400" />
              Éditeur de Code Sécurisé
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="border-yellow-500 bg-yellow-900/20">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-yellow-100">
                <strong>Attention :</strong> Toute modification du code sera journalisée et require une confirmation.
                Seuls les super-administrateurs peuvent accéder à cette fonctionnalité.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Fichier à éditer</label>
              <select 
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded text-white"
                onChange={(e) => setCodeContent(e.target.value)}
              >
                <option value="">Sélectionner un fichier...</option>
                <option value="config/app.py">Configuration Application</option>
                <option value="config/database.py">Configuration Base de Données</option>
                <option value="config/security.py">Configuration Sécurité</option>
                <option value="templates/email.html">Template Email</option>
                <option value="static/css/custom.css">Styles Personnalisés</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Contenu du fichier</label>
              <textarea
                value={codeContent}
                onChange={(e) => setCodeContent(e.target.value)}
                className="w-full h-96 p-3 bg-slate-900 border border-slate-600 rounded text-green-400 font-mono text-sm"
                placeholder="# Sélectionnez un fichier pour commencer à éditer..."
                style={{ fontFamily: 'Monaco, Consolas, "Courier New", monospace' }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCodeEditor(false);
                  setCodeContent('');
                }}
                className="border-slate-600"
              >
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  // Handle code save with confirmation
                  setConfirmAction({ type: 'code', content: codeContent });
                  setShowConfirmDialog(true);
                }}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={!codeContent.trim()}
              >
                <Save className="h-4 w-4 mr-2" />
                Sauvegarder Code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;